import { useEffect, useMemo, useState } from 'react';
import {
  BUSINESS_COUNTRIES,
  BUSINESS_TYPES,
  DAY_CODES,
  DAY_NAMES,
  PRICE_RANGES,
  normalizeBusiness,
  type BusinessDetails,
  type DayCode
} from '@sarnia/shared';
import { editorStore, useEditorSelector } from '../../editorStore';
import { Icon } from '../../../components/ui/Icon';
import type { PanelCtx } from '../panelTypes';
import { businessOnPage } from '../businessOnPage';
import './businessPanel.css';

type Draft = Omit<BusinessDetails, 'hours'> & { hours?: BusinessDetails['hours'] };

const TITLE_MAX = 60;
const DESCRIPTION_MIN = 70;
const DESCRIPTION_MAX = 160;

function hostOf(url: string | null): string {
  if (!url) return 'your-business.sarnia.digital';
  try {
    return new URL(url).host;
  } catch {
    return url;
  }
}

export default function BusinessPanel({ ctx, defaults }: { ctx: PanelCtx; defaults: Record<string, string> | null }) {
  const meta = useEditorSelector((state) => state.site?.meta);
  const publishUrl = useEditorSelector((state) => state.publishUrl);
  // The owner's typing is kept as is; only what passes the checks is saved (so a half-typed phone number never blocks saving).
  const [draft, setDraft] = useState<Draft>(() => ({ ...(ctx.props.business ?? {}) }));
  const [applied, setApplied] = useState<string | null>(null);

  useEffect(() => {
    // Undo/redo or a restore replaced the saved details: start again from them.
    setDraft((current) => {
      const saved = normalizeBusiness(current).value;
      return JSON.stringify(saved ?? {}) === JSON.stringify(ctx.props.business ?? {}) ? current : { ...(ctx.props.business ?? {}) };
    });
  }, [ctx.props.business]);

  const result = useMemo(() => normalizeBusiness(draft), [draft]);
  const errorFor = (field: string) => result.errors.find((e) => e.path === `business.${field}` || e.path.startsWith(`business.${field}.`))?.message;

  const change = (patch: Partial<Draft>) => {
    const next = { ...draft, ...patch };
    setDraft(next);
    setApplied(null);
    ctx.update({ business: normalizeBusiness(next).value });
  };

  const setDay = (day: DayCode, times: { opens: string; closes: string } | null) => {
    const hours = { ...(draft.hours ?? {}) };
    if (times) hours[day] = times;
    else delete hours[day];
    change({ hours });
  };

  const copyToWeekdays = () => {
    const monday = draft.hours?.Mo;
    if (!monday) return;
    change({ hours: { ...(draft.hours ?? {}), Tu: monday, We: monday, Th: monday, Fr: monday } });
  };

  const putOnPage = () => {
    const saved = result.value;
    if (!saved || !defaults) return;
    const { patch, changes } = businessOnPage(saved, ctx.props, ctx.entry, defaults);
    if (changes > 0) {
      ctx.update(patch);
      ctx.reload();
    }
    setApplied(
      changes > 0
        ? `Updated ${changes} ${changes === 1 ? 'place' : 'places'} on the page. Addresses are worded differently on every template, so check those yourself.`
        : 'Nothing to update: the page has no sample phone numbers or emails left.'
    );
  };

  const title = meta?.title ?? '';
  const description = meta?.description ?? '';
  const field = (key: 'name' | 'phone' | 'email' | 'street' | 'town' | 'region' | 'postcode' | 'areaServed', label: string, extra: Record<string, string> = {}) => (
    <div className="dw-field">
      <label className="dw-label" htmlFor={`dwb-${key}`}>
        {label}
      </label>
      <input
        id={`dwb-${key}`}
        className={`dw-input${errorFor(key) ? ' dwi-invalid' : ''}`}
        value={draft[key] ?? ''}
        onChange={(e) => change({ [key]: e.target.value })}
        aria-invalid={errorFor(key) ? true : undefined}
        aria-describedby={errorFor(key) ? `dwb-${key}-error` : undefined}
        {...extra}
      />
      {errorFor(key) ? (
        <p className="dwi-error" id={`dwb-${key}-error`} role="alert">
          {errorFor(key)}
        </p>
      ) : null}
    </div>
  );

  return (
    <div className="dw-panel">
      <div className="dw-panel-heading">
        <Icon name="globe" size={18} />
        <h2>Business</h2>
      </div>
      <p className="dw-help-text">
        Where you are and when you're open. Search engines read these from your page, which helps you show up in local
        searches and on maps.
      </p>

      <section className="dw-card">
        <h3 className="dw-card-title">How you'll look on Google</h3>
        <div className="dwb-serp" aria-label="Search result preview">
          <span className="dwb-serp-url">{hostOf(publishUrl)}</span>
          <span className="dwb-serp-title">{title || 'Your business name'}</span>
          <span className="dwb-serp-desc">{description || 'Add a description: one or two sentences about what you do and where.'}</span>
        </div>
        <div className="dw-field">
          <label className="dw-label" htmlFor="dwb-title">
            Title <span className={`dwb-count${title.length > TITLE_MAX ? ' dwb-count--over' : ''}`}>{title.length}/{TITLE_MAX}</span>
          </label>
          <input id="dwb-title" className="dw-input" value={title} onChange={(e) => editorStore.updateMeta({ title: e.target.value })} />
        </div>
        <div className="dw-field">
          <label className="dw-label" htmlFor="dwb-description">
            Description{' '}
            <span className={`dwb-count${description.length > DESCRIPTION_MAX ? ' dwb-count--over' : ''}`}>
              {description.length}/{DESCRIPTION_MAX}
            </span>
          </label>
          <textarea
            id="dwb-description"
            className="dw-input dw-textarea"
            value={description}
            onChange={(e) => editorStore.updateMeta({ description: e.target.value })}
          />
          {description.length > 0 && description.length < DESCRIPTION_MIN ? (
            <p className="dw-help-text">A little longer works better: say what you do and where, e.g. "Sourdough bakery in St Peter Port".</p>
          ) : null}
        </div>
      </section>

      <section className="dw-card">
        <h3 className="dw-card-title">About the business</h3>
        <div className="dw-field">
          <label className="dw-label" htmlFor="dwb-type">
            Kind of business
          </label>
          <select id="dwb-type" className="dw-input dw-select" value={draft.type ?? ''} onChange={(e) => change({ type: e.target.value || undefined })}>
            <option value="">Choose…</option>
            {BUSINESS_TYPES.map((type) => (
              <option key={type.id} value={type.id}>
                {type.label}
              </option>
            ))}
          </select>
        </div>
        {field('name', 'Business name (if different from the title)')}
        {field('phone', 'Phone', { type: 'tel', autoComplete: 'tel', placeholder: '01481 123456' })}
        {field('email', 'Email', { type: 'email', autoComplete: 'email' })}
        <button type="button" className="dw-button dw-button--ghost" onClick={putOnPage} disabled={!result.value?.phone && !result.value?.email}>
          Put my phone and email on the page
        </button>
        {applied ? (
          <p className="dw-help-text" role="status">
            {applied}
          </p>
        ) : null}
      </section>

      <section className="dw-card">
        <h3 className="dw-card-title">Address</h3>
        {field('street', 'Street', { autoComplete: 'address-line1' })}
        <div className="dwb-row">
          {field('town', 'Town', { autoComplete: 'address-level2' })}
          {field('postcode', 'Postcode', { autoComplete: 'postal-code' })}
        </div>
        <div className="dw-field">
          <label className="dw-label" htmlFor="dwb-country">
            Country
          </label>
          <select id="dwb-country" className="dw-input dw-select" value={draft.country ?? ''} onChange={(e) => change({ country: e.target.value || undefined })}>
            <option value="">Choose…</option>
            {BUSINESS_COUNTRIES.map((country) => (
              <option key={country.id} value={country.id}>
                {country.label}
              </option>
            ))}
          </select>
        </div>
        {field('areaServed', 'Areas you cover (trades, deliveries)', { placeholder: 'e.g. Guernsey and Herm' })}
      </section>

      <section className="dw-card">
        <h3 className="dw-card-title">Opening hours</h3>
        <div className="dwb-hours">
          {DAY_CODES.map((day) => {
            const times = draft.hours?.[day];
            return (
              <div key={day} className="dwb-day">
                <label className="dwb-day-name">
                  <input type="checkbox" checked={Boolean(times)} onChange={(e) => setDay(day, e.target.checked ? { opens: '09:00', closes: '17:00' } : null)} />
                  {DAY_NAMES[day]}
                </label>
                {times ? (
                  <span className="dwb-times">
                    <input
                      type="time"
                      className="dw-input"
                      aria-label={`${DAY_NAMES[day]} opens`}
                      value={times.opens}
                      onChange={(e) => e.target.value && setDay(day, { ...times, opens: e.target.value })}
                    />
                    <span aria-hidden="true">–</span>
                    <input
                      type="time"
                      className="dw-input"
                      aria-label={`${DAY_NAMES[day]} closes`}
                      value={times.closes}
                      onChange={(e) => e.target.value && setDay(day, { ...times, closes: e.target.value })}
                    />
                  </span>
                ) : (
                  <span className="dwb-closed">Closed</span>
                )}
              </div>
            );
          })}
        </div>
        {draft.hours?.Mo ? (
          <button type="button" className="dw-check-link" onClick={copyToWeekdays}>
            Use Monday's hours for Tuesday to Friday
          </button>
        ) : null}
      </section>

      <section className="dw-card">
        <h3 className="dw-card-title">Prices</h3>
        <div className="dw-field">
          <label className="dw-label" htmlFor="dwb-price">
            Price range
          </label>
          <select id="dwb-price" className="dw-input dw-select" value={draft.priceRange ?? ''} onChange={(e) => change({ priceRange: e.target.value || undefined })}>
            <option value="">Don't say</option>
            {PRICE_RANGES.map((range) => (
              <option key={range} value={range}>
                {range}
              </option>
            ))}
          </select>
        </div>
      </section>
    </div>
  );
}
